import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import FileUpload from '../components/FileUpload';
import { createPayee, updatePayee, uploadDocument, getDocuments, deleteDocument, getPaymentMethods } from '../services/api';
import { useToken } from '../hooks/useToken';
import { useCurrentUser, isOnyxFC } from '../context/UserContext';
import type { Payee, Document, PaymentMethod } from '../types';

interface Props {
  mode: 'new' | 'edit';
}

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DC','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY',
];

export default function PayeeForm({ mode }: Props) {
  const getToken = useToken();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { user } = useCurrentUser();
  const onyxFC = isOnyxFC(user);

  /*
   * v1 workaround: edit mode pre-populates from React Router navigation state
   * passed by PayeesList when navigating to /payees/:id/edit. Direct URL
   * navigation to this route is not supported because GET /api/payees/{id}
   * does not exist yet. Future session: implement that endpoint and replace
   * this approach with a proper API fetch so direct-link navigation works.
   */
  const passedPayee: Payee | null =
    mode === 'edit'
      ? ((location.state as { payee?: Payee } | null)?.payee ?? null)
      : null;

  // Form state — seeded from passedPayee when editing
  const [payeeName, setPayeeName] = useState(passedPayee?.payee_name ?? '');
  const [email, setEmail] = useState(passedPayee?.email ?? '');
  const [phone, setPhone] = useState(passedPayee?.phone ?? '');
  const [address1, setAddress1] = useState(passedPayee?.address_line1 ?? '');
  const [address2, setAddress2] = useState(passedPayee?.address_line2 ?? '');
  const [city, setCity] = useState(passedPayee?.city ?? '');
  const [state, setState] = useState(passedPayee?.state_code ?? '');
  const [zip, setZip] = useState(passedPayee?.zip_code ?? '');
  const [methodId, setMethodId] = useState<number | null>(passedPayee?.preferred_method_id ?? null);
  const [hasContract, setHasContract] = useState<boolean | null>(
    passedPayee ? passedPayee.contract_required : null,
  );
  const [files, setFiles] = useState<File[]>([]);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [existingDocs, setExistingDocs] = useState<Document[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    getToken()
      .then((t) => getPaymentMethods(t))
      .then(setPaymentMethods)
      .catch(() => { /* non-fatal — dropdown stays empty */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (mode !== 'edit' || !passedPayee) return;
    getToken()
      .then((t) => getDocuments(t, undefined, passedPayee.payee_id))
      .then(setExistingDocs)
      .catch(() => { /* non-fatal — list stays empty */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleDocumentDelete(documentId: number) {
    const token = await getToken();
    await deleteDocument(token, documentId);
    setExistingDocs((prev) => prev.filter((d) => d.document_id !== documentId));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!payeeName.trim()) { setError('Payee name is required.'); return; }
    if (hasContract === null) { setError('Contract Required selection is required.'); return; }
    setError(null);
    setSubmitting(true);

    try {
      const token = await getToken();
      const body = {
        payee_name: payeeName.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        address_line1: address1.trim() || null,
        address_line2: address2.trim() || null,
        city: city.trim() || null,
        state_code: state || null,
        zip_code: zip.trim() || null,
        has_sow: false,
        contract_required: hasContract as boolean,
        preferred_method_id: methodId,
      };

      let payeeId: number;
      if (mode === 'new') {
        const res = await createPayee(token, body);
        payeeId = res.payee_id;
      } else {
        payeeId = Number(id);
        await updatePayee(token, payeeId, body);
      }

      // Upload supporting docs
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('payee_id', String(payeeId));
        fd.append('document_type', 'Other');
        await uploadDocument(token, fd).catch(console.error);
      }

      navigate('/payees');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (mode === 'edit' && !passedPayee) {
    return (
      <Layout pageTitle="Edit a Payee">
        <div className="max-w-xl">
          <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
            Payee data unavailable. Please return to the{' '}
            <button
              onClick={() => navigate('/payees')}
              className="underline font-medium hover:text-yellow-900"
            >
              Payees list
            </button>{' '}
            and select Edit.
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle={mode === 'new' ? 'Add a New Payee' : 'Edit a Payee'}>
      <div className="max-w-xl">
        <form onSubmit={handleSubmit} className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          {/* Payee Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Payee Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={payeeName}
              onChange={(e) => setPayeeName(e.target.value)}
              required
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Address */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">Mailing Address</label>
            <input
              type="text"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              placeholder="Address Line 1"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
              placeholder="Address Line 2 (optional)"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <div className="flex gap-3">
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="City"
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder="ZIP"
                maxLength={10}
                className="w-28 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {/* Preferred Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Preferred Payment Method</label>
            <select
              value={methodId ?? ''}
              onChange={(e) => setMethodId(Number(e.target.value) || null)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">— Select —</option>
              {paymentMethods.map((m) => (
                <option key={m.payment_method_id} value={m.payment_method_id}>{m.method_name}</option>
              ))}
            </select>
          </div>

          {/* Contract Required */}
          <div>
            <label htmlFor="contract" className="block text-sm font-medium text-gray-700 mb-1">
              Contract Required <span className="text-red-500">*</span>
            </label>
            <select
              id="contract"
              value={hasContract === null ? '' : hasContract ? 'yes' : 'no'}
              onChange={(e) => {
                const v = e.target.value;
                setHasContract(v === '' ? null : v === 'yes');
              }}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">-- Select --</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>

          {/* Supporting Documents */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Supporting Documents</label>

            {existingDocs.length > 0 && (
              <ul className="mb-3 space-y-1">
                {existingDocs.map((doc) => (
                  <li key={doc.document_id} className="flex items-center justify-between text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded px-3 py-1.5">
                    <span className="truncate mr-3">{doc.file_name}</span>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <a
                        href={doc.download_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-blue-600 hover:text-blue-800 font-medium"
                      >
                        View
                      </a>
                      {onyxFC && (
                        <button
                          type="button"
                          onClick={() => handleDocumentDelete(doc.document_id)}
                          className="text-red-500 hover:text-red-700 font-medium"
                          title="Delete document"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}

            <FileUpload onFiles={(f) => setFiles((prev) => [...prev, ...f])} />
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs text-gray-600">
                    <span>{f.name}</span>
                    <button
                      type="button"
                      onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))}
                      className="text-red-500 hover:text-red-700 ml-3"
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50"
            >
              {submitting ? 'Saving...' : mode === 'new' ? 'Add Payee' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/payees')}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
